import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const COLORS = ['#3B82F6', '#8B5CF6', '#F59E0B', '#10B981', '#EC4899', '#F97316', '#06B6D4', '#84CC16'];

// All approval status options from the finance workflow
const APPROVAL_STATUSES = [
  { value: 'not_set', label: 'Not Set', bg: 'bg-gray-100', text: 'text-gray-700' },
  { value: 'login', label: 'Login', bg: 'bg-blue-100', text: 'text-blue-700' },
  { value: 'fi_pending', label: 'FI Pending', bg: 'bg-orange-100', text: 'text-orange-700' },
  { value: 'bank_statement_pending', label: 'Bank Statement Pending', bg: 'bg-yellow-100', text: 'text-yellow-700' },
  { value: 'clarification_pending', label: 'Clarification Pending', bg: 'bg-amber-100', text: 'text-amber-700' },
  { value: 'enach_pending', label: 'E-Nach Pending', bg: 'bg-purple-100', text: 'text-purple-700' },
  { value: 'ipr_invoice_pending', label: 'IPR/Invoice Pending', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { value: 'disbursement_pending', label: 'Disbursement Pending', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  { value: 'disbursement_done', label: 'Disbursement Done', bg: 'bg-green-100', text: 'text-green-700' },
  { value: 'rejected', label: 'Rejected', bg: 'bg-red-100', text: 'text-red-700' }
];

// Get status style by value
const getStatusStyle = (status) => {
  if (!status || status === 'None' || status === 'null' || status === '') {
    return { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Set' };
  }
  const normalized = status.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  const found = APPROVAL_STATUSES.find(s => s.value === normalized || s.label.toLowerCase() === status.toLowerCase());
  return found || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
};

// Date format helper - DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dateStr;
  }
};

// Get first day of current month in YYYY-MM-DD format (timezone safe)
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

// Get today in YYYY-MM-DD format (timezone safe)
const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format currency
const formatCurrency = (amount) => {
  if (!amount) return '₹0';
  const num = parseFloat(amount);
  if (isNaN(num)) return '₹0';
  return '₹' + num.toLocaleString('en-IN');
};

function FinanciersReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({
    metrics: {},
    financier_breakdown: [],
    table_data: [],
    filter_options: { financiers: [], loan_statuses: [] }
  });
  const [dateRange, setDateRange] = React.useState({
    from: getFirstDayOfMonth(),
    to: getToday()
  });
  const [filters, setFilters] = React.useState({
    loan_status: 'all',
    financier: 'all'
  });
  const [activeView, setActiveView] = React.useState('table'); // 'table' or 'chart'

  React.useEffect(() => {
    fetchData();
  }, [dateRange, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('date_from', dateRange.from);
      if (dateRange.to) params.append('date_to', dateRange.to);
      if (filters.loan_status !== 'all') params.append('loan_status', filters.loan_status);
      if (filters.financier !== 'all') params.append('financier', filters.financier);
      
      const response = await axios.get(`${BACKEND_URL}/api/leads/analytics/financiers?${params.toString()}`, getAuthHeader());
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    }
    setLoading(false);
  };

  const { metrics, financier_breakdown, table_data, filter_options } = data;

  // Prepare chart data
  const pieData = financier_breakdown.slice(0, 8).map((item, idx) => ({
    name: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name,
    value: item.total,
    color: COLORS[idx % COLORS.length]
  }));

  const statusChartData = [
    { name: 'Approved', value: metrics.approved || 0, color: '#22C55E' },
    { name: 'Pending', value: metrics.pending || 0, color: '#F59E0B' },
    { name: 'Rejected', value: metrics.rejected || 0, color: '#EF4444' }
  ];

  // Export to Excel
  const exportToExcel = () => {
    const exportData = table_data.map(item => ({
      'Booking No': item.booking_no || '-',
      'Customer': item.customer_name,
      'Mobile': item.mobile,
      'Model': item.model || '-',
      'Financier': item.financier || '-',
      'Status': getStatusStyle(item.loan_status).label,
      'Disbursement': item.disbursement_amount || 0,
      'Loan A/C': item.loan_account_no || '-',
      'Date': item.booking_date || '-',
      'Sales Rep': item.sales_rep || '-'
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financiers Report');
    XLSX.writeFile(wb, `Financiers_Report_${dateRange.from}_to_${dateRange.to}.xlsx`);
    toast.success('Report exported to Excel');
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Financiers Report">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="report-title">Financiers Report</h2>
            <p className="text-sm text-gray-500">Analyze loan approvals and disbursements by financier • <span className="font-medium text-gray-700">{metrics.total_financed || 0} financed records</span></p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Date Filters */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                data-testid="date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                data-testid="date-to"
              />
            </div>
            <div className="relative group">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                📥 Export
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border hidden group-hover:block z-10">
                <button onClick={exportToExcel} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📊 Export to Excel</button>
                <button onClick={() => window.print()} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📄 Print/PDF</button>
              </div>
            </div>
            <button
              onClick={() => navigate('/leads')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium">Approval Status:</label>
            <select
              value={filters.loan_status}
              onChange={(e) => setFilters(prev => ({ ...prev, loan_status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              {APPROVAL_STATUSES.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium">Financier:</label>
            <select
              value={filters.financier}
              onChange={(e) => setFilters(prev => ({ ...prev, financier: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 min-w-[200px]"
              data-testid="financier-filter"
            >
              <option value="all">All Financiers</option>
              {filter_options.financiers.map(f => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setActiveView('table')}
              className={`px-4 py-2 rounded-lg transition ${activeView === 'table' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              📋 Table
            </button>
            <button
              onClick={() => setActiveView('chart')}
              className={`px-4 py-2 rounded-lg transition ${activeView === 'chart' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              📊 Charts
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {/* Metrics Cards - 2-row layout */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Row 1 - Key numbers */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-testid="metric-total-financed">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                  </div>
                  <p className="text-xs text-blue-600 font-medium">Total Financed</p>
                </div>
                <p className="text-2xl font-bold text-blue-700">{metrics.total_financed || 0}</p>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-200" data-testid="metric-disbursement-amt">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-xs text-purple-600 font-medium">Disbursement Amt</p>
                </div>
                <p className="text-2xl font-bold text-purple-700">{formatCurrency(metrics.total_disbursement)}</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200" data-testid="metric-disbursement-done">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-xs text-green-600 font-medium">Disbursement Done</p>
                </div>
                <p className="text-2xl font-bold text-green-700">{metrics.disbursement_done || 0}</p>
              </div>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200" data-testid="metric-completion-rate">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <p className="text-xs text-teal-600 font-medium">Completion Rate</p>
                </div>
                <p className="text-2xl font-bold text-teal-700">{metrics.completion_rate || 0}%</p>
              </div>

              {/* Row 2 - Status breakdown */}
              <div className="bg-cyan-50 rounded-xl p-4 border border-cyan-200" data-testid="metric-disb-pending">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-xs text-cyan-600 font-medium">Disb. Pending</p>
                </div>
                <p className="text-2xl font-bold text-cyan-700">{metrics.disbursement_pending || 0}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border border-orange-200" data-testid="metric-login">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" /></svg>
                  </div>
                  <p className="text-xs text-orange-600 font-medium">Login</p>
                </div>
                <p className="text-2xl font-bold text-orange-700">{metrics.login || 0}</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200" data-testid="metric-fi-pending">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  </div>
                  <p className="text-xs text-yellow-600 font-medium">FI Pending</p>
                </div>
                <p className="text-2xl font-bold text-yellow-700">{metrics.fi_pending || 0}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200" data-testid="metric-rejected">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-xs text-red-600 font-medium">Rejected</p>
                </div>
                <p className="text-2xl font-bold text-red-700">{metrics.rejected || 0}</p>
              </div>
            </div>

            {activeView === 'chart' ? (
              /* Charts View */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Financier Distribution Pie */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Distribution by Financier</h3>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-gray-400">No data available</div>
                  )}
                </div>

                {/* Status Distribution */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Approval Status Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statusChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" name="Count">
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Financier Breakdown Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden md:col-span-2">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-800">Financier-wise Breakdown</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Financier</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Approved</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Pending</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Rejected</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Disbursement</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {financier_breakdown.map((item, idx) => (
                          <tr key={item.name} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                                <span className="font-medium text-gray-800">{item.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center font-medium">{item.total}</td>
                            <td className="px-6 py-4 text-center">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">{item.approved}</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">{item.pending}</span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm">{item.rejected}</span>
                            </td>
                            <td className="px-6 py-4 text-center font-medium text-purple-700">
                              {formatCurrency(item.disbursement_amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              /* Table View */
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-800">Finance Details ({table_data.length} records)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="finance-table">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Financier</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Disbursement</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan A/C</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Rep</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {table_data.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="px-6 py-16 text-center text-gray-500">
                            No finance records found for the selected filters
                          </td>
                        </tr>
                      ) : (
                        table_data.map(row => {
                          const statusStyle = getStatusStyle(row.loan_status);
                          return (
                            <tr key={row.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <span className="text-blue-600 font-medium">#{row.booking_no}</span>
                              </td>
                              <td className="px-4 py-3">
                                <div>
                                  <p className="font-medium text-gray-800">{row.customer_name || '-'}</p>
                                  <p className="text-xs text-gray-500">{row.mobile || ''}</p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{row.model || '-'}</td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-medium text-purple-700">{row.financier}</span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                                  {statusStyle.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-green-700">
                                {formatCurrency(row.disbursement_amount)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{row.loan_account_no || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(row.booking_date)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{row.sales_rep || '-'}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </LeadsLayout>
  );
}

export default FinanciersReport;
